import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'motion/react'
import { Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import { OUT_EASE } from '@/components/landing/service/halftone'
import { fragHair, fragMuted, useStream } from './fragments'

/** Illustrative sheet: four regions that add up to the total row. */
const ROWS: Array<[string, number]> = [
  ['North', 54000],
  ['South', 38000],
  ['East', 62000],
  ['West', 46000],
]
const TOTAL = ROWS.reduce((sum, [, v]) => sum + v, 0)
const fmt = (n: number) => n.toLocaleString('en-US')

/** Splits the demo answer "=FORMULA(...)  Explanation." into its two parts. */
function parseDemo(answer: string) {
  const match = /^(=.+?\))\s{2,}(.+)$/.exec(answer)
  return match ? { formula: match[1]!, explanation: match[2]! } : { formula: answer, explanation: '' }
}

/**
 * Full-width Excel window under the giant word: the formula bar types the
 * SUMIFS formula from the page's demo, the region cells fill, and the total
 * row adds them up; the Templatean pane explains the formula. Static and
 * complete under reduced motion. Decorative.
 */
export function SheetHero({ title, demo }: { title: string; demo: { ask: string; answer: string } }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { amount: 0.3 })
  const reduce = useReducedMotion()
  const { formula, explanation } = parseDemo(demo.answer)
  const { shown, done } = useStream(formula, inView, 1, 38)
  const filled = reduce || done

  const cell = cn('border-b border-r px-3 py-2.5 md:px-4 md:py-3', fragHair)
  const head = cn(cell, 'bg-[var(--color-landing-gray-50)] text-xs font-semibold uppercase tracking-wide dark:bg-[#15151d]', fragMuted)

  return (
    <div
      ref={ref}
      aria-hidden
      className="relative bg-white text-[13px] text-[var(--color-landing-gray-900)] shadow-[0_40px_80px_-40px_rgba(10,10,10,0.45)] outline outline-1 -outline-offset-1 outline-[var(--color-landing-gray-200)] dark:bg-[#1c1c26] dark:text-white dark:outline-white/10 md:text-[15px]"
    >
      {/* Window bar */}
      <div className={cn('flex items-center gap-3 border-b px-4 py-3', fragHair)}>
        <OfficeAppIcon app="excel" className="h-7 w-7" />
        <span className="font-semibold">{title}</span>
        <span className={cn('ml-auto hidden text-sm sm:inline', fragMuted)}>Q4 sales by region</span>
      </div>

      {/* Formula bar */}
      <div className={cn('flex items-stretch border-b font-[family-name:var(--font-landing-mono)]', fragHair)}>
        <span className={cn('flex w-14 shrink-0 items-center justify-center border-r font-semibold md:w-20', fragHair)}>B2</span>
        <span className={cn('flex shrink-0 items-center border-r px-3 italic', fragHair, fragMuted)}>fx</span>
        <span className="min-w-0 flex-1 overflow-hidden px-3 py-3 [overflow-wrap:anywhere] md:py-3.5">
          {shown}
          {!done && <span className="ml-[1px] inline-block h-[1em] w-[0.45em] translate-y-[0.15em] bg-[#107c41]" />}
        </span>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_22rem]">
        {/* Grid */}
        <div className="overflow-hidden">
          <div className="grid grid-cols-[2.25rem_minmax(0,1.2fr)_minmax(0,1fr)] sm:grid-cols-[3rem_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <span className={head} />
            <span className={head}>A · Region</span>
            <span className={cn(head, 'text-right')}>B · Q4 sales</span>
            <span className={cn(head, 'hidden text-right sm:block')}>C · Share</span>
            {ROWS.map(([region, value], i) => (
              <div key={region} className="contents">
                <span className={cn(cell, 'bg-[var(--color-landing-gray-50)] text-center text-xs dark:bg-[#15151d]', fragMuted)}>{i + 2}</span>
                <span className={cell}>{region}</span>
                <span
                  className={cn(
                    cell,
                    'relative text-right tabular-nums',
                    i === 0 && 'outline outline-2 -outline-offset-2 outline-[#107c41]'
                  )}
                >
                  <motion.span
                    className="inline-block"
                    initial={false}
                    animate={{ opacity: filled ? 1 : 0, y: filled ? 0 : 6 }}
                    transition={{ duration: 0.4, ease: OUT_EASE, delay: filled && !reduce ? 0.1 + i * 0.12 : 0 }}
                  >
                    {fmt(value)}
                  </motion.span>
                </span>
                <span className={cn(cell, 'hidden text-right tabular-nums sm:block', fragMuted)}>
                  {Math.round((value / TOTAL) * 100)}%
                </span>
              </div>
            ))}
            <span className={cn(cell, 'bg-[var(--color-landing-gray-50)] text-center text-xs dark:bg-[#15151d]', fragMuted)}>6</span>
            <span className={cn(cell, 'font-bold')}>Total</span>
            <span className={cn(cell, 'text-right font-bold tabular-nums')}>
              <motion.span
                className="inline-block"
                initial={false}
                animate={{ opacity: filled ? 1 : 0 }}
                transition={{ duration: 0.4, ease: OUT_EASE, delay: filled && !reduce ? 0.7 : 0 }}
              >
                {fmt(TOTAL)}
              </motion.span>
            </span>
            <span className={cn(cell, 'hidden text-right font-bold tabular-nums sm:block')}>100%</span>
          </div>
        </div>

        {/* Templatean pane */}
        <div className={cn('hidden flex-col gap-4 border-l p-5 lg:flex', fragHair)}>
          <span className="text-sm font-bold tracking-[-0.01em]">
            templ<span className="text-[var(--color-landing-accent)]">a</span>tean
          </span>
          <div className={cn('border p-3', fragHair)}>
            <span className={cn('block text-xs font-semibold uppercase tracking-wide', fragMuted)}>Ask</span>
            <span className="mt-1 block font-medium">{demo.ask}</span>
          </div>
          <motion.div
            className="flex gap-2 bg-[var(--color-landing-gray-50)] p-3 dark:bg-[#15151d]"
            initial={false}
            animate={{ opacity: filled ? 1 : 0.25 }}
            transition={{ duration: 0.5, ease: OUT_EASE, delay: filled && !reduce ? 0.9 : 0 }}
          >
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-landing-accent)]" />
            <span className="text-sm leading-snug">{explanation}</span>
          </motion.div>
          <span className="mt-auto self-start bg-[#107c41] px-3 py-2 text-xs font-bold uppercase text-white">Insert formula</span>
        </div>
      </div>
    </div>
  )
}
